import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Chip, Dialog, DialogContent, DialogTitle, DialogActions, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { useZeny } from './ZenyProvider';
import ZenyStatus from './ZenyStatus';
import { SEVERITY_ORDER } from './severity';
import { ageLabel, occurredAt } from './issueModel';
import { evidencePairs } from './evidence';
import useOperationalPermissions from '../common/util/operationalPermissions';
import { formatDateTime } from '../common/util/datetime';
import DownloadTable from '../shell/DownloadTable';

const IssueTable = ({ items, defaultStatus = 'OPEN' }) => {
  const { mutate, error: serviceError } = useZeny();
  const { operate } = useOperationalPermissions();
  const navigate = useNavigate();
  const [severity, setSeverity] = useState(''); const [status, setStatus] = useState(defaultStatus); const [search, setSearch] = useState('');
  const [resolution, setResolution] = useState(null); const [resolutionNote, setResolutionNote] = useState(''); const [outcome, setOutcome] = useState('FIXED');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0); const [detail, setDetail] = useState(null); const [pending, setPending] = useState(false); const [error, setError] = useState(null);
  const filtered = items.filter((i) => (!severity || i.severity === severity) && (!status || (status === 'OPEN' ? i.status !== 'RESOLVED' : i.status === status)) && `${i.title} ${i.vehicleReference} ${i.tripId} ${i.site || ''}`.toLowerCase().includes(search.toLowerCase())).sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity) || Date.parse(a.occurredAt || a.raisedAt) - Date.parse(b.occurredAt || b.raisedAt));
  const urgentOpen = items.filter((i) => i.status !== 'RESOLVED' && ['CRITICAL', 'HIGH'].includes(i.severity));
  const oldestUrgent = urgentOpen.reduce((oldest, issue) => {
    const oldestTime = Date.parse(occurredAt(oldest)) || Infinity;
    const issueTime = Date.parse(occurredAt(issue)) || Infinity;
    return issueTime < oldestTime ? issue : oldest;
  }, urgentOpen[0]);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(filtered.length / pageSize) - 1));
  const act = async (id, verb, body = {}) => { setPending(true); setError(null); try { await mutate(id, verb, body); setDetail(null); setResolution(null); } catch (err) { setError(err.message); } finally { setPending(false); } };
  return (
    <>
      <ZenyStatus />
      <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
        <TextField label="Search issues" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ flex: '1 1 180px' }} />
        <TextField label="Severity" select value={severity} onChange={(e) => { setSeverity(e.target.value); setPage(0); }} sx={{ minWidth: 140 }}>
          <MenuItem value="">All severities</MenuItem>
          {SEVERITY_ORDER.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField label="Issue status" select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }} sx={{ minWidth: 150 }}>{[['OPEN', 'Open'], ['', 'All statuses'], ['RAISED', 'Raised'], ['ACKNOWLEDGED', 'Acknowledged'], ['RESOLVED', 'Resolved']].map(([v, label]) => <MenuItem key={v} value={v}>{label}</MenuItem>)}</TextField>
      </Stack>
      {!!urgentOpen.length && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {`${urgentOpen.length} urgent open ${urgentOpen.length === 1 ? 'issue' : 'issues'} — oldest outstanding for ${ageLabel(occurredAt(oldestUrgent))}.`}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <DownloadTable
          name="exceptions"
          rows={filtered}
          columns={[
            ['Severity', (i) => i.severity],
            ['Issue', (i) => i.title],
            ['Vehicle or equipment', (i) => i.vehicleReference || (i.readerId ? `Reader ${i.readerId}` : 'Site equipment')],
            ['Trip', (i) => (i.tripId ? `Q-${i.tripId}` : '')],
            ['Occurred at', (i) => (occurredAt(i) ? formatDateTime(occurredAt(i)) : '')],
            ['Status', (i) => i.status.toLowerCase()],
            ['Owner', (i) => i.ownerName || ''],
          ]}
        />
      </Stack>
      <TableContainer sx={{ maxHeight: 390 }}>
        <Table size="small" stickyHeader sx={{ minWidth: 700 }}>
          <TableHead><TableRow>{['Severity', 'Issue / vehicle', 'Occurred at', 'Age', 'Status', 'Actions'].map((c) => <TableCell key={c}>{c}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {filtered.slice(currentPage * pageSize, currentPage * pageSize + pageSize).map((i) => (
              <TableRow key={i.id} hover>
                <TableCell><Chip size="small" label={i.severity} color={i.severity === 'CRITICAL' ? 'error' : i.severity === 'HIGH' ? 'warning' : 'default'} /></TableCell>
                <TableCell>
                  <Button onClick={() => setDetail(i)} sx={{ textAlign: 'left', justifyContent: 'start' }}>{i.title}</Button>
                  <Typography variant="caption" display="block">
                    {i.vehicleReference || (i.readerId ? `Reader ${i.readerId}` : 'Site equipment')}
                    {i.tripId ? ` · Q-${i.tripId}` : ''}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{occurredAt(i) ? formatDateTime(occurredAt(i)) : 'Unknown'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="body2" fontWeight={i.status !== 'RESOLVED' && ['CRITICAL', 'HIGH'].includes(i.severity) ? 700 : 400}>{ageLabel(occurredAt(i))}</Typography></TableCell>
                <TableCell>
                  {i.status.toLowerCase()}
                  {i.ownerName && <Typography variant="caption" display="block">{`Owner: ${i.ownerName}`}</Typography>}
                </TableCell>
                <TableCell>
                  <Stack direction="row">
                    {operate && i.status === 'RAISED' && <Button disabled={pending || !!serviceError} onClick={() => act(i.id, 'acknowledge')}>Acknowledge</Button>}
                    {operate && i.status !== 'RESOLVED' && <Button disabled={pending || !!serviceError} onClick={() => { setResolution(i); setResolutionNote(''); setOutcome('FIXED'); setError(null); }}>Resolve</Button>}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!filtered.length && !serviceError && <Typography sx={{ py: 3 }} color="text.secondary">No issues match this selection.</Typography>}
      <TablePagination component="div" count={filtered.length} page={currentPage} rowsPerPage={pageSize} rowsPerPageOptions={[10, 25, 50]} onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} onPageChange={(_, p) => setPage(p)} />
      <Dialog open={!!resolution} onClose={() => !pending && setResolution(null)} fullWidth maxWidth="sm">
        <form onSubmit={(e) => { e.preventDefault(); act(resolution.id, 'resolve', { note: resolutionNote, outcome }); }}>
          <DialogTitle>Resolve exception</DialogTitle>
          <DialogContent>
            <Stack gap={2} sx={{ pt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <Typography>{resolution?.title}</Typography>
              <TextField select label="Outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)}>{[['FIXED', 'Fixed'], ['FALSE_ALARM', 'False alarm'], ['ACCEPTED_RISK', 'Risk accepted']].map(([value, name]) => <MenuItem key={value} value={value}>{name}</MenuItem>)}</TextField>
              <TextField required multiline minRows={3} label="What was done and why" value={resolutionNote} onChange={(e) => setResolutionNote(e.target.value)} inputProps={{ maxLength: 500 }} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={pending} onClick={() => setResolution(null)}>Cancel</Button>
            <Button type="submit" disabled={pending || !resolutionNote.trim()}>Resolve</Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="sm">
        <DialogTitle>{detail?.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{detail?.occurredAt || detail?.raisedAt ? formatDateTime(detail.occurredAt || detail.raisedAt) : 'Occurrence time unknown'}</Typography>
          {detail?.resolutionNote && <Typography sx={{ mb: 2 }}>{`Resolution: ${detail.resolutionOutcome} · ${detail.resolutionNote}`}</Typography>}
          {evidencePairs(detail?.evidence).map(([name, value]) => <Typography key={name} sx={{ mb: 1 }}>{`${name}: ${value}`}</Typography>)}
        </DialogContent>
        <DialogActions>
          {/* An issue on a trip opens that trip, whose own map carries the recorded trail. One
              raised against a vehicle with no open trip - an offline tracker, say - has nowhere
              more specific to go than the plant map. */}
          {!!detail?.tripId && <Button onClick={() => navigate(`/trips/${detail.tripId}`)}>Open trip</Button>}
          {!detail?.tripId && !!detail?.vehicleId && <Button onClick={() => navigate('/plant-tracking')}>Open plant map</Button>}
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default IssueTable;
